import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid, Typography, Divider, Hidden, Chip, Button } from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import { stringify } from 'query-string';
import emoji from 'node-emoji';

// CONSTANTS
import { ROUTE_DISCOVER_SEARCH, ROUTE_START } from '@/constants/routes';
import { LOCATIONS_APP_PAGE } from '@/constants/locations';

// UTILITY
import { routeEnhance } from '@/utility/routes';
import { analyticsInternalLinks } from '@/utility/analytics';

// HOC
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import Link from '@/components/Link';
import AppDescription from '@/components/AppDescription';

// STYLES
import {
  Content,
  AboutContent,
  DevicesContent,
  GenderContent,
  DevicesGrid,
  GenresGrid,
  ListWrapper,
  RatingListWrapper,
  RatingsList,
  CordCut,
} from './styles';
import { Container } from '../../styles';

class About extends PureComponent {
  handleGenre = name => {
    const {
      router: { navigate },
      partner,
    } = this.props;
    const partnerSettings = partner?.settings || {};

    // if have partner
    // and
    // if partner haven't access to marketplace
    // break the function (return)
    if (!partnerSettings.use_marketplace) {
      return;
    }

    const qsParams = { genres: [name.toLowerCase()], page: 1, type: ['free', 'paid'] };

    const search = stringify(qsParams, {
      arrayFormat: 'comma',
      skipEmptyString: true,
    });

    const routeEnhanced = routeEnhance(`${ROUTE_DISCOVER_SEARCH}?${search}`, {
      state: { location: LOCATIONS_APP_PAGE },
    });

    analyticsInternalLinks({
      unique_id: routeEnhanced.state.unique_id,
      from_page_path: routeEnhanced.state.from_page_path,
      from_search_params: routeEnhanced.state.from_search_params,
      to_page_path: routeEnhanced.pathname,
      to_search_params: routeEnhanced.search,
      partner_id: routeEnhanced.state.partner_id,
      location: routeEnhanced.state.location,
    });

    navigate(routeEnhanced);
  };

  render() {
    const { t, data, partner, unique_id } = this.props;
    const hasProssCons =
      !!data?.infoList?.pross_and_cons?.pross.length ||
      !!data?.infoList?.pross_and_cons?.cons.length;
    const hasPartner = !!Object.keys(partner).length;
    const partnerSettings = partner?.settings || {};
    let use_fmb = true;

    if (partner?.partner_slug) {
      use_fmb = !!partnerSettings?.use_fmb;
    }

    return (
      <div>
        <Container className="page-content">
          <Typography className="title" variant="h3">
            {t('appDetails.about.title', { app_name: data.app.name })}
          </Typography>
          <div>
            <Grid container spacing={4} justifyContent="space-between">
              <Grid item xs={12} md={7}>
                <AboutContent>
                  <AppDescription text={data.app.long_desc || ''} showCollapse />
                </AboutContent>
                {Boolean(data.app?.device_grouping?.length) && (
                  <DevicesContent>
                    <Typography className="title" variant="h5">
                      {t('appDetails.about.devices')}
                    </Typography>

                    <DevicesGrid container spacing={3} alignItems="center">
                      {data.app.device_grouping.map(device => {
                        return (
                          <Grid key={device.device_grouping_id} item>
                            <img src={device.logo_url} alt={device.device_grouping_name} />
                          </Grid>
                        );
                      })}
                    </DevicesGrid>
                  </DevicesContent>
                )}
                {Boolean(data.app?.genres?.length) && (
                  <GenderContent className="genre-wrapper">
                    <Typography className="title" variant="h5">
                      {t('appDetails.about.genres')}
                    </Typography>

                    <GenresGrid container spacing={2}>
                      {data.app.genres.map(genre => {
                        return (
                          <Grid key={genre.genre_id} item>
                            <Chip
                              label={genre.genre_name}
                              onClick={() => this.handleGenre(genre.genre_slug)}
                            />
                          </Grid>
                        );
                      })}
                    </GenresGrid>
                  </GenderContent>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                {!!data?.infoList?.feature.length && (
                  <Content>
                    <Typography className="title" variant="h5">
                      {t('appDetails.about.keyFeatures')}
                    </Typography>
                    <ListWrapper>
                      <ul className="pros-cons-list">
                        {data.infoList.feature.map((item, index) => (
                          <li className="pcl-item" key={index}>
                            <i className="icon-check" />
                            {item.text}
                          </li>
                        ))}
                      </ul>
                    </ListWrapper>
                    <Hidden smDown>
                      <Divider />
                    </Hidden>
                  </Content>
                )}

                {hasProssCons ? (
                  <Content>
                    <Typography className="title" variant="h5">
                      {t('appDetails.about.prosCons')}
                    </Typography>
                    <ListWrapper>
                      <ul className="pros-cons-list">
                        {data.infoList.pross_and_cons.pross.map((item, index) => (
                          <li className="pcl-item" key={index}>
                            <i className={item.type === 'pross' ? 'icon-check' : 'icon-minus'} />
                            {item.text}
                          </li>
                        ))}
                        {data.infoList.pross_and_cons.cons.map((item, index) => (
                          <li className="pcl-item" key={index}>
                            <i className={item.type === 'pross' ? 'icon-check' : 'icon-minus'} />
                            {item.text}
                          </li>
                        ))}
                      </ul>
                    </ListWrapper>
                    <Hidden smDown>
                      <Divider />
                    </Hidden>
                  </Content>
                ) : null}

                {!!data?.ratings?.appRatings && (
                  <Content>
                    <Typography className="title" variant="h5">
                      {t('appDetails.about.ratings')}
                    </Typography>
                    <RatingListWrapper>
                      <RatingsList>
                        {data.ratings.appRatings.map((item, index) => (
                          <li key={index}>
                            <div className="service p4">
                              {item.title}
                              <span className="count">({item.count})</span>
                            </div>
                            <div>
                              <Rating value={item.rating} precision={0.1} readOnly />
                            </div>
                          </li>
                        ))}
                      </RatingsList>
                    </RatingListWrapper>
                  </Content>
                )}
                {!hasPartner && !unique_id ? (
                  <Content>
                    <CordCut className="cord-cut">
                      <div className="cord-cut__content">
                        <Typography className="cord-cut__title" variant="h5">
                          {t('appDetails.about.cordCut.title')}&nbsp;
                          <span className="cord-cut__scissors">{emoji.get(':scissors:')}</span>
                        </Typography>
                        <p className="p4 m-b-15">{t('appDetails.about.cordCut.subtitle')}</p>
                        {use_fmb && (
                          <Button
                            className="cord-cut__btn"
                            component={Link}
                            variant="contained"
                            size="small"
                            to={ROUTE_START}
                            endIcon={<i className="icon-arrow-right" />}
                            location={LOCATIONS_APP_PAGE}
                          >
                            {t('buttons.takeTheSurvey')}
                          </Button>
                        )}
                      </div>
                      <div className="cord-cut__triangle" />
                    </CordCut>
                  </Content>
                ) : null}
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>
    );
  }
}

About.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
  isIframe: PropTypes.bool.isRequired,
  unique_id: PropTypes.string.isRequired,
};

export default compose(withTranslation(), withRouter)(About);
